import { Box, Typography } from '@mui/material'
import { AlertCircleIcon } from '@untitled-theme/icons-react'
import { useEffect, useRef, useState } from 'react'

import { BedarfVerbrauchLabel, BedarfVerbrauchType } from '@/shared/components/BedarfVerbrauchLabel'
import InfoChip from '@/shared/components/InfoChip'
import type { EnergyLabel } from '@/shared/types'
import { ENERGY_LABEL_COLORS } from '@/shared/utils/energyLabelColors'

interface EnergieklasseProps {
  currentRating: EnergyLabel
  potentialRating?: EnergyLabel
  itemHeight?: number
  itemGap?: number
  indicatorHeight?: number
}

const Energieklasse = ({
  currentRating,
  potentialRating,
  itemHeight = 15,
  itemGap = 2,
  indicatorHeight = 18,
}: EnergieklasseProps) => {
  const currentRatingRef = useRef<HTMLDivElement>(null)
  const [currentRatingLeft, setCurrentRatingLeft] = useState<number>(0)

  useEffect(() => {
    if (currentRatingRef.current) {
      setCurrentRatingLeft(currentRatingRef.current.offsetLeft)
    }
  }, [currentRating])
  return (
    <Box
      sx={{
        flex: { sm: '1' },
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        border: '1px solid #E1E1E4',
        padding: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
          }}
        >
          Energieklasse
        </Typography>
        <BedarfVerbrauchLabel type={BedarfVerbrauchType.BEDARF} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: '3px',
          position: 'relative',
        }}
      >
        {/* Energy scale items - fill remaining space */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${itemGap}px`,
            flex: 1,
          }}
        >
          {[
            { label: 'A+', widthPercent: 20 },
            { label: 'A', widthPercent: 30 },
            { label: 'B', widthPercent: 40 },
            { label: 'C', widthPercent: 50 },
            { label: 'D', widthPercent: 60 },
            { label: 'E', widthPercent: 70 },
            { label: 'F', widthPercent: 80 },
            { label: 'G', widthPercent: 90 },
            { label: 'H', widthPercent: 100 },
          ].map((item, index) => (
            <Box
              key={index}
              sx={{
                width: `${item.widthPercent}%`,
                height: `${itemHeight}px`,
                backgroundColor: ENERGY_LABEL_COLORS[item.label as EnergyLabel],
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '8px',
                borderRadius: '2px 0 0 2px',
                clipPath:
                  'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%)',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '8px',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Indicator and Aktuelle - positioned based on currentRating */}
        <Box
          ref={currentRatingRef}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '3px',
            position: 'relative',
            top: (() => {
              const ratings = ['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']
              const index = ratings.indexOf(currentRating)
              // Center the indicator on the item: item position - (indicator height - item height) / 2
              const itemPosition = index * (itemHeight + itemGap)
              const centerOffset = (indicatorHeight - itemHeight) / 2
              return `${itemPosition - centerOffset}px`
            })(),
          }}
        >
          {/* Indicator */}
          <Box
            sx={{
              width: '28px',
              height: `${indicatorHeight}px`,
              backgroundColor: '#181D29',
              display: 'flex',
              alignItems: 'center',
              position: 'relative',
              borderRadius: '0 2px 2px 0',
              clipPath: 'polygon(28% 0%, 100% 0%, 100% 100%, 28% 100%, 0% 50%)',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '9px',
                position: 'absolute',
                left: '65%',
                transform: 'translateX(-50%)',
              }}
            >
              {currentRating}
            </Typography>
          </Box>

          {/* Aktuelle text */}
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '10px',
              color: '#181D29',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Aktuelle
          </Typography>
        </Box>

        {/* Indicator and Potenzial - absolutely positioned based on potentialRating */}
        {potentialRating && potentialRating !== currentRating && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
              position: 'absolute',
              left: `${currentRatingLeft}px`,
              top: (() => {
                const ratings = ['A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H']
                const index = ratings.indexOf(potentialRating)
                // Center the indicator on the item: item position - (indicator height - item height) / 2
                const itemPosition = index * (itemHeight + itemGap)
                const centerOffset = (indicatorHeight - itemHeight) / 2
                return `${itemPosition - centerOffset}px`
              })(),
            }}
          >
            {/* Indicator */}
            <Box
              sx={{
                width: '28px',
                height: `${indicatorHeight}px`,
                backgroundColor: ENERGY_LABEL_COLORS[potentialRating],
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                borderRadius: '0 2px 2px 0',
                clipPath: 'polygon(28% 0%, 100% 0%, 100% 100%, 28% 100%, 0% 50%)',
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '9px',
                  position: 'absolute',
                  left: '65%',
                  transform: 'translateX(-50%)',
                }}
              >
                {potentialRating}
              </Typography>
            </Box>

            {/* Potenzial text */}
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: '10px',
                color: '#181D29',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Potenzial
            </Typography>
          </Box>
        )}

        {potentialRating && potentialRating === currentRating && (
          <Box
            sx={{
              position: 'absolute',
              right: '0px',
              top: `${itemHeight / 2}px`,
              transform: 'translateY(-50%)',
            }}
          >
            <InfoChip
              label="Keine Verbesserung"
              icon={<AlertCircleIcon style={{ width: '16px', height: '16px', color: '#464A54' }} />}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default Energieklasse
